import moment from "moment";
import { ConfigType } from "./interface";
import { XAXisOption } from "echarts/types/dist/shared";

/**
 * x轴刻度生成函数
 * @param opt option配置项
 * @param config
 * @returns
 */
export const xAxisIntervalFormat = (opt, config: ConfigType = {}) => {
  // 刻度处理
  opt.xAxis[0].axisLabel.formatter = function (value: any) {
    return config.formatter ? config.formatter(value) : value;
  };
  const xAxisData = opt?.xAxis[0]?.data;
  const count = xAxisData.length;
  const defaultInterval = parseInt(((count - 1) / 24).toString());
  let differNum = count - defaultInterval * 24 - 1;
  let preIndex = 0;
  const intervalFormat = (index: number) => {
    // 小于等于24个点全部展示
    if (count <= 24) {
      return true;
    }
    if (index === count - 1) {
      // 遍历完数据重置（防止数据被刷掉）
      differNum = count - defaultInterval * 24 - 1;
      preIndex = 0;
      return true;
    }
    if (index === 0) {
      return true;
    }
    // 间隔数
    const currentInterval = differNum > 0 ? defaultInterval : defaultInterval - 1;
    if (preIndex + 1 + currentInterval === index) {
      differNum = differNum > 0 ? differNum - 1 : 0;
      preIndex = index;
      return true;
    }
    return false;
  };
  opt.xAxis[0].axisLabel.interval = intervalFormat;
  opt.xAxis[0].axisTick.interval = intervalFormat;
  // realtime 5分钟时间滑块刻度自适应事件
  const _chart = config?.chart || null;
  if (_chart) {
    _chart.on('datazoom', (params: any) => {
      if (params?.start === 0 && params?.end === 100) {
        opt.xAxis[0].axisLabel.interval = intervalFormat;
        opt.xAxis[0].axisTick.interval = intervalFormat;
        _chart.setOption(opt);
      } else {
        opt.xAxis[0].axisLabel.interval = 'auto';
        opt.xAxis[0].axisTick.interval = 'auto';
        _chart.setOption(opt);
      }
    });
  }
  return opt;
};



/**
 * x轴高亮显示刻度
 * @param opt option配置项
 * @param config isRealtime 是否realTime折线图
 * @returns
 */
export const xAxisHighLight = (opt, config: ConfigType = { isRealtime: false }) => {
  if (!config?.isRealtime) {
    opt.series = opt.series?.map((seriesItem: any) => {
      if (seriesItem?.type === 'line') {
        const seriesData = seriesItem?.data?.map((item: any, index: any) => {
          if (typeof item === 'object') return item;
          // 是否是单独的圆点
          const isAloneSymbol = (seriesItem?.data[index - 1] === '' || seriesItem?.data[index - 1] === undefined) &&
            (seriesItem?.data[index + 1] === '' || seriesItem?.data[index + 1] === undefined);
          return {
            value: item,
            itemStyle: {
              opacity: isAloneSymbol ? 1 : 0
            },
            symbol: 'circle'
          };
        });
        return {
          ...seriesItem,
          data: seriesData,
          symbolSize: 10,
          emphasis: {
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 1,
              borderType: 'solid',
              opacity: 1
            },
          }
        };
      } 
      return seriesItem;
      
    });
  }

  opt.tooltip.axisPointer = {
    z: 0,
    label: {
      show: true,
      // backgroundColor: 'transparent',
      backgroundColor: '#ffffff',
      fontSize: 12,
      color: '#262832',
      fontWeight: 700,
      formatter: (params: { value: any; }) => config.formatter ? config.formatter(params) : params.value
    },
    lineStyle: config?.lineStyle || {
      color: '#cccccc',
      width: 1,
      type: 'solid'
    },
  };
  return opt;
};

/**
 * x轴刻度展示每月的1号和每个周日的日期刻度
 * @param option option配置项
 * @param dayOfWeek 展示周几为刻度(默认周日)
 * @returns 每个月的1号刻度以及月份名称 & 每个周日的日期刻度
 */
export const xAxisMonthAndSundayFormat = (opt: { xAxis?: XAXisOption | XAXisOption[]; }, dayOfWeek = 0) => {
  const monthAndSundayFormatX = (value: string | number) => {
    if (!moment(value).isValid()) return ""; // 非法日期返回空字符串

    const date = moment(value);
    const day = date.date(); // 获取日期 (1-31)
    const isSunday = date.day() === dayOfWeek; // 检查是否是周日

    if (day !== 1 && !isSunday) return ""; // 只显示每月的 1 号和周日

    const monthLabel = day === 1 ? `{monthStyle|${date.format('MMMM')}}` : ""; // 1 号显示月份名称
    return `${date.format("MM-DD")}\n${monthLabel}`;
  };

  if (opt.xAxis) {
    const xAxisArray: XAXisOption[] = Array.isArray(opt.xAxis) ? opt.xAxis : [opt.xAxis];

    opt.xAxis = xAxisArray.map((item) => ({
      ...item,
      axisLabel: {
        ...(item.axisLabel || {}),
        formatter: (value: string | number) => monthAndSundayFormatX(value),
        rich: {
          monthStyle: {
            fontWeight: 700,
            color: "#525464",
            fontSize: "10px",
            lineHeight: 20,
          },
        },
        interval(_index, value) {
          const date = moment(value);
          return date.date() === 1 || date.day() === dayOfWeek; // 只展示 1 号和周日刻度
        },
      },
    }));
  }

  return opt;
};


/**
 * x轴刻度展示每月的1号
 * @param option option配置项
 * @returns 每个月的1号刻度以及对应的季度名称
 */
export const xAxisMonthAndQuarterFormat = (opt: { xAxis?: XAXisOption | XAXisOption[]; }) => {
  const monthAndQuarterFormatX = (value: string | number) => {
    if (!moment(value).isValid()) return ""; // 非法日期返回空字符串

    const date = moment(value);
    const day = date.date(); // 获取日期 (1-31)
    const month = date.month() + 1; // 获取月份 (1-12)

    if (day !== 1) return ""; // 只显示每月的 1 号

    const quarterMap: Record<number, string> = {
      1: "Q1",
      4: "Q2",
      7: "Q3",
      10: "Q4",
    };
    const quarterLabel = quarterMap[month] ? `\n{quarterStyle|${quarterMap[month]}}` : "";

    return `${date.format("MM-DD")} ${quarterLabel}`;
  };

  if (opt.xAxis) {
    const xAxisArray: XAXisOption[] = Array.isArray(opt.xAxis) ? opt.xAxis : [opt.xAxis];

    opt.xAxis = xAxisArray.map((item) => ({
      ...item,
      axisLabel: {
        ...(item.axisLabel || {}),
        formatter: (value: string | number) => monthAndQuarterFormatX(value),
        rich: {
          quarterStyle: {
            fontWeight: 700,
            color: '#525464',
            fontSize: '10px',
            lineHeight: 20
          },
        },
        interval(_index, value) {
          return moment(value).date() === 1; // 只展示每月的 1 号刻度
        },
      },
    }));
  }

  return opt;
};
